import React, { useState, useEffect } from "react";
import { HiPlus, HiXMark, HiArrowPath } from "react-icons/hi2";
import {
  ActivityType,
  GlobalActivityType,
  GlobalActivityCategory,
  ActivityVariableDefinition,
} from "../../types/activity";
import { activityService } from "../../services/activityService";
import { GlobalActivityService } from "../../services/globalActivityService";
import { useAuth } from "../../contexts/AuthContext";
import CreateActivityTypeDialog from "../sessions/CreateActivityTypeDialog";
import { getDefaultUnit } from "../../utils/unitUtils";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useThemeStore } from "../../stores/themeStore";

export interface ActivitySelectorProps {
  /** Callback when a single activity is added */
  onAdd: (activityType: ActivityType) => void;
  /** Callback for bulk add (receives array of ActivityTypes) */
  onBulkAdd?: (activityTypes: ActivityType[]) => void;
  /** Organisation ID - if provided, fetches coach activity types instead of user activity types */
  organisationId?: string;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Whether to show the bulk select option */
  showBulkSelect?: boolean;
  /** Whether to allow creating new activity types */
  allowCreate?: boolean;
  /** Title for the section */
  title?: string;
  /** Placeholder text for search input */
  placeholder?: string;
  /** ID of the last selected activity (for "Last used" indicator) */
  lastSelectedActivityId?: string;
  /** Custom className for the container */
  className?: string;
  /** Whether to render as a fixed floating button */
  isFloatingButton?: boolean;
  /** Increment to open the picker from a parent (e.g. per-group Add exercise). */
  openRequestId?: number;
  /** Called when the picker closes, whether or not an activity was added. */
  onClosed?: () => void;
}

const ActivitySelector: React.FC<ActivitySelectorProps> = ({
  onAdd,
  onBulkAdd,
  organisationId,
  disabled = false,
  showBulkSelect = true,
  allowCreate = true,
  title = "Add Exercise",
  placeholder,
  lastSelectedActivityId: externalLastSelectedId,
  className = "",
  isFloatingButton = false,
  openRequestId,
  onClosed,
}) => {
  // console.log("[ActivitySelector] Component rendering!");
  const { currentUser, userData, authState } = useAuth();
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === "peakx-dark";
  const isImperial = authState.user?.isImperial ?? false;
  // console.log(
  //   "[ActivitySelector] userData?.adminActivityCatIDs:",
  //   userData?.adminActivityCatIDs,
  // );

  // Activity types state
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [globalPackActivityTypes, setGlobalPackActivityTypes] = useState<
    ActivityType[]
  >([]);
  const [globalCategories, setGlobalCategories] = useState<
    GlobalActivityCategory[]
  >([]);
  const [isLoadingActivityTypes, setIsLoadingActivityTypes] =
    useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Selection state
  const [selectedActivityTypeId, setSelectedActivityTypeId] =
    useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all"); // "all", "custom", or pack ID
  const [lastSelectedActivityId, setLastSelectedActivityId] = useState<string>(
    externalLastSelectedId || "",
  );
  const [bulkSelectionMode, setBulkSelectionMode] = useState<boolean>(true);
  const [selectedActivityIds, setSelectedActivityIds] = useState<Set<string>>(
    new Set(),
  );
  const [isCreateActivityDialogOpen, setIsCreateActivityDialogOpen] =
    useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  // Stringify the pack IDs for stable dependency comparison
  const userPackIdsString = JSON.stringify(userData?.adminActivityCatIDs || []);

  // Reset state when modal opens
  useEffect(() => {
    if (isModalOpen) {
      setSearchTerm("");
      setCategoryFilter("all");
      setSelectedActivityTypeId("");
      setSelectedActivityIds(new Set());
      setBulkSelectionMode(true);
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (openRequestId != null && openRequestId > 0) {
      setIsModalOpen(true);
    }
  }, [openRequestId]);

  const closeModal = () => {
    setIsModalOpen(false);
    onClosed?.();
  };

  // Fetch global categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categories = await GlobalActivityService.fetchCategories();
        setGlobalCategories(categories);
      } catch (error) {
        console.error("[ActivitySelector] Error fetching categories:", error);
        // Don't fail the whole component if categories fail to load
      }
    };
    fetchCategories();
  }, []);

  // Fetch activity types
  const fetchActivityTypes = async () => {
    if (!currentUser?.uid) return;

    try {
      setIsLoadingActivityTypes(true);
      setLoadError(null);

      let types: ActivityType[];
      if (organisationId) {
        types = await activityService.fetchCoachActivityTypes(organisationId);
      } else {
        types = await activityService.fetchActivityTypes(currentUser.uid);
      }

      setActivityTypes(types);

      // Also fetch activities from user's added activity packs
      const userPackIds: string[] = JSON.parse(userPackIdsString);
      // console.log("[ActivitySelector] User pack IDs:", userPackIds);

      if (userPackIds.length > 0) {
        try {
          // console.log(
          //   "[ActivitySelector] Fetching activities for packs:",
          //   userPackIds,
          // );
          const globalTypes =
            await GlobalActivityService.fetchActivityTypesForCategories(
              userPackIds,
            );
          // console.log(
          //   "[ActivitySelector] Fetched global activities:",
          //   globalTypes.length,
          // );

          // Convert GlobalActivityType to ActivityType format
          // Also convert variable units to match user's measurement system
          const convertedTypes: ActivityType[] = globalTypes.map(
            (gt: GlobalActivityType) => ({
              id: `global_${gt.categoryId}_${gt.id}`, // Prefix to distinguish from user activities
              name: gt.name,
              description: gt.description,
              // Convert variable units to user's measurement system
              variables: gt.variables.map((v): ActivityVariableDefinition => {
                if (typeof v === "string") {
                  // Legacy string format - convert to definition with user's preferred unit
                  return {
                    name: v,
                    defaultUnit: getDefaultUnit(v, isImperial),
                  };
                }
                // Already a definition - update defaultUnit to user's preference
                return {
                  ...v,
                  defaultUnit: getDefaultUnit(v.name, isImperial),
                };
              }),
              image: gt.image,
              isFromPack: true, // Flag to identify pack activities
              categoryId: gt.categoryId,
            }),
          );
          // console.log(
          //   "[ActivitySelector] Converted pack activities:",
          //   convertedTypes.length,
          // );
          setGlobalPackActivityTypes(convertedTypes);
        } catch (packError) {
          console.error(
            "[ActivitySelector] Error fetching pack activity types:",
            packError,
          );
          // Don't fail the whole load if pack activities fail
          setGlobalPackActivityTypes([]);
        }
      } else {
        // console.log("[ActivitySelector] No pack IDs, clearing pack activities");
        setGlobalPackActivityTypes([]);
      }
    } catch (error) {
      console.error("Error fetching activity types:", error);
      setLoadError("Failed to load activity types");
    } finally {
      setIsLoadingActivityTypes(false);
    }
  };

  // Fetch on mount and when user/org/packs/measurement system changes
  useEffect(() => {
    // console.log(
    //   "[ActivitySelector] Effect triggered, userPackIdsString:",
    //   userPackIdsString,
    // );
    fetchActivityTypes();
  }, [currentUser?.uid, organisationId, userPackIdsString, isImperial]);

  // Sync external lastSelectedActivityId
  useEffect(() => {
    if (externalLastSelectedId) {
      setLastSelectedActivityId(externalLastSelectedId);
    }
  }, [externalLastSelectedId]);

  // Combine user activities with global pack activities
  const allActivityTypes = React.useMemo(() => {
    // console.log("[ActivitySelector] Combining activities:");
    // console.log("[ActivitySelector] - User activities:", activityTypes.length);
    // console.log(
    //   "[ActivitySelector] - Global pack activities:",
    //   globalPackActivityTypes.length,
    // );

    // Filter out global pack activities that have the same name as user's own activities
    const userActivityNames = new Set(
      activityTypes.map((a) => a.name.toLowerCase()),
    );
    const filteredPackActivities = globalPackActivityTypes.filter(
      (ga) => !userActivityNames.has(ga.name.toLowerCase()),
    );

    const combined = [...activityTypes, ...filteredPackActivities];
    // console.log(
    //   "[ActivitySelector] - Filtered pack activities (excluding duplicates):",
    //   filteredPackActivities.length,
    // );
    // console.log(
    //   "[ActivitySelector] - Total activities for dropdown:",
    //   combined.length,
    // );

    if (filteredPackActivities.length > 0) {
      // console.log(
      //   "[ActivitySelector] Pack activities added to dropdown:",
      //   filteredPackActivities.map((a) => a.name),
      // );
    }

    return combined;
  }, [activityTypes, globalPackActivityTypes]);

  // Get unique pack categories from globalPackActivityTypes with actual category names
  const packCategories = React.useMemo(() => {
    const categoriesMap = new Map<
      string,
      { id: string; name: string; count: number }
    >();

    // Create a map of category ID to category name for quick lookup
    const categoryNameMap = new Map<string, string>();
    globalCategories.forEach((cat) => {
      if (cat.id) {
        categoryNameMap.set(cat.id, cat.name);
      }
    });

    globalPackActivityTypes.forEach((activity) => {
      if (activity.categoryId) {
        const existing = categoriesMap.get(activity.categoryId);
        if (existing) {
          existing.count++;
        } else {
          // Use the actual category name from globalCategories, fallback to ID if not found
          const categoryName =
            categoryNameMap.get(activity.categoryId) || activity.categoryId;
          categoriesMap.set(activity.categoryId, {
            id: activity.categoryId,
            name: categoryName,
            count: 1,
          });
        }
      }
    });

    return Array.from(categoriesMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [globalPackActivityTypes, globalCategories]);

  // Filter activity types based on search term, category filter, and prioritize last selected
  const filteredActivityTypes = allActivityTypes
    .filter((activityType) => {
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = activityType.name
        .toLowerCase()
        .includes(searchLower);

      // Category filter
      let matchesCategory = true;
      if (categoryFilter === "custom") {
        matchesCategory = !activityType.isFromPack;
      } else if (categoryFilter !== "all") {
        // Filter by specific pack category ID
        matchesCategory = activityType.categoryId === categoryFilter;
      }

      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      // Put last selected activity at the top
      if (a.id === lastSelectedActivityId) return -1;
      if (b.id === lastSelectedActivityId) return 1;
      // Put user's own activities before pack activities
      if (a.isFromPack !== b.isFromPack) {
        return a.isFromPack ? 1 : -1;
      }
      // Otherwise maintain alphabetical order
      return a.name.localeCompare(b.name);
    });

  // Handlers
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setSelectedActivityTypeId("");
  };

  const handleActivitySelect = (activityType: ActivityType) => {
    setSelectedActivityTypeId(activityType.id!);
    // Don't set search term - keep the list visible
  };

  const handleAddActivity = () => {
    if (!selectedActivityTypeId) return;

    const activityType = allActivityTypes.find(
      (type) => type.id === selectedActivityTypeId,
    );
    if (!activityType) return;

    onAdd(activityType);
    setLastSelectedActivityId(selectedActivityTypeId);
  };

  const handleCreateActivity = () => {
    setIsCreateActivityDialogOpen(true);
  };

  const handleActivityTypeCreated = async (
    activityTypeId: string,
    activityTypeName: string,
  ) => {
    // Refresh activity types list
    await fetchActivityTypes();

    // Keep Add Activity modal open and focus the newly created activity in the list.
    setIsModalOpen(true);
    setSearchTerm(activityTypeName);
    setCategoryFilter("all");
    setSelectedActivityTypeId(activityTypeId);
    setSelectedActivityIds(new Set([activityTypeId]));
    setLastSelectedActivityId(activityTypeId);
  };

  const handleBulkActivityToggle = (activityId: string) => {
    const newSet = new Set(selectedActivityIds);
    if (newSet.has(activityId)) {
      newSet.delete(activityId);
    } else {
      newSet.add(activityId);
    }
    setSelectedActivityIds(newSet);
  };

  const handleBulkAddActivities = () => {
    if (!onBulkAdd || selectedActivityIds.size === 0) return;

    const selectedTypes: ActivityType[] = [];
    selectedActivityIds.forEach((activityId) => {
      const activityType = allActivityTypes.find(
        (type) => type.id === activityId,
      );
      if (activityType) {
        selectedTypes.push(activityType);
      }
    });

    if (selectedTypes.length > 0) {
      onBulkAdd(selectedTypes);

      // Update last selected if only one was added
      if (selectedActivityIds.size === 1) {
        const firstId = Array.from(selectedActivityIds)[0];
        setLastSelectedActivityId(firstId);
      }

      // Clear selection
      setSelectedActivityIds(new Set());
      setBulkSelectionMode(false);
    }
  };

  const isDisabled = isLoadingActivityTypes || disabled;
  const defaultPlaceholder = `Search ${allActivityTypes.length} exercises...`;
  const showCreateButton =
    allowCreate && searchTerm && filteredActivityTypes.length === 0;
  const canAdd =
    selectedActivityTypeId &&
    allActivityTypes.find((type) => type.id === selectedActivityTypeId);

  // Log when dropdown would be rendered
  // console.log("[ActivitySelector] Render state:", {
  //   isLoading: isLoadingActivityTypes,
  //   totalActivities: allActivityTypes.length,
  //   userActivities: activityTypes.length,
  //   packActivities: globalPackActivityTypes.length,
  //   filteredForDropdown: filteredActivityTypes.length,
  // });

  return (
    <>
      {isFloatingButton ? (
        /* Floating + Button - Fixed on mobile, absolute within modal on desktop */
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          disabled={isDisabled}
          className="fixed md:absolute right-6 bottom-24 md:right-4 md:bottom-[18px] w-11 h-11 flex items-center justify-center rounded-full shadow-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-base-content text-base-100 hover:bg-base-content/90 z-[9999] md:z-10"
        >
          <HiPlus className="w-4 h-4" />
        </button>
      ) : (
        <div className={className}>
          {/* Add Activity Button */}
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            disabled={isDisabled}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium font-grotesk bg-base-200 text-base-content border border-base-300 rounded-md hover:bg-base-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <HiPlus className="text-sm" />
            Add Exercise
          </button>
        </div>
      )}

      {/* Activity Selection Modal */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[10001]"
          onClose={closeModal}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-75"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-stretch sm:items-center sm:justify-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-100"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-75"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full h-full sm:h-auto sm:max-w-md transform overflow-hidden sm:rounded-md bg-base-100 shadow-xl transition-all flex flex-col sm:max-h-[90vh]">
                {/* Header */}
                <div className="flex items-center justify-between p-8 pb-4 flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <Dialog.Title className="text-xl font-grotesk font-semibold text-base-content">
                      {title}
                    </Dialog.Title>
                    <button
                      onClick={() => {
                        setSearchTerm("");
                        setSelectedActivityTypeId("");
                        setSelectedActivityIds(new Set());
                        setBulkSelectionMode(false);
                      }}
                      className="p-2 text-base-content/50 hover:text-base-content/70 rounded-full hover:bg-base-300 transition-colors"
                      title="Refresh"
                    >
                      <HiArrowPath className="text-lg" />
                    </button>
                  </div>
                  <button
                    onClick={closeModal}
                    className="p-2 text-base-content/50 hover:text-base-content/70 rounded-full hover:bg-base-300 transition-colors"
                  >
                    <HiXMark className="text-xl" />
                  </button>
                </div>

                {/* Fixed Content - Search and Category Filter */}
                <div className="px-8 flex-shrink-0">
                  {!loadError && !isLoadingActivityTypes && (
                    <>
                      <div className="relative mb-3">
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(e) => handleSearchChange(e.target.value)}
                          placeholder={placeholder || defaultPlaceholder}
                          className="input bg-base-300 text-[16px] sm:text-sm placeholder:text-base-content/60 font-bold placeholder:font-light w-full focus:outline-none focus:none focus:none focus:none border-base-300 border-1 p-3 pr-10 rounded-md font-sans"
                          disabled={isDisabled}
                        />
                        {searchTerm && (
                          <button
                            type="button"
                            onClick={() => setSearchTerm("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/50 hover:text-base-content/70 transition-colors"
                            disabled={isDisabled}
                          >
                            <HiXMark className="text-lg" />
                          </button>
                        )}
                      </div>

                      {/* Category Filter Dropdown */}
                      <div className="relative mb-3">
                        <select
                          value={categoryFilter}
                          onChange={(e) => setCategoryFilter(e.target.value)}
                          className="select bg-base-300 text-[16px] sm:text-sm w-full focus:outline-none border-base-300 border-1 py-3 px-3 rounded-md font-sans font-medium text-base-content/80 leading-normal h-auto"
                          disabled={isDisabled}
                        >
                          <option value="all">All Exercises</option>
                          <option value="custom">My Custom Exercises</option>
                          {packCategories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name} ({category.count})
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                </div>

                {/* Scrollable Content - Activity List */}
                <div className="px-8 flex-1 overflow-y-auto">
                  {loadError ? (
                    <div className="text-sm text-red-500 font-grotesk">
                      {loadError}
                      <button
                        type="button"
                        onClick={fetchActivityTypes}
                        className="ml-2 text-base-content/70 hover:text-base-content underline"
                      >
                        Retry
                      </button>
                    </div>
                  ) : isLoadingActivityTypes ? (
                    <div className="text-sm text-base-content/60 font-grotesk">
                      Loading exercises...
                    </div>
                  ) : (
                    <>
                      {filteredActivityTypes.length > 0 ? (
                        <div className="rounded-md bg-base-100 p-2">
                          {filteredActivityTypes.map((activityType) => (
                            <label
                              key={activityType.id}
                              className={`flex items-center gap-3 p-2 py-4 hover:bg-base-200 rounded cursor-pointer border-b ${
                                selectedActivityIds.has(activityType.id!)
                                  ? "bg-base-300"
                                  : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedActivityIds.has(
                                  activityType.id!,
                                )}
                                onChange={() =>
                                  handleBulkActivityToggle(activityType.id!)
                                }
                                className="checkbox"
                                disabled={isDisabled}
                              />
                              <span className="text-md text-base-content font-semibold font-sans">
                                {activityType.name}
                              </span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <div className="py-6 text-center">
                          <p className="text-sm text-base-content/60 font-sans">
                            No exercises found matching "{searchTerm}"
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Footer - Fixed Add Button */}
                <div className="pb-16 px-8  pt-4 flex-shrink-0">
                  {!loadError && !isLoadingActivityTypes && (
                    <button
                      type="button"
                      onClick={() => {
                        if (showCreateButton) {
                          handleCreateActivity();
                          return;
                        }

                        handleBulkAddActivities();
                        closeModal();
                      }}
                      disabled={Boolean(
                        isDisabled ||
                        (!showCreateButton && selectedActivityIds.size === 0),
                      )}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-white bg-neutral rounded-lg hover:bg-neutral/90 transition-colors disabled:opacity-10 disabled:cursor-not-allowed font-grotesk"
                    >
                      <HiPlus className="text-sm" />
                      {showCreateButton
                        ? `Create "${searchTerm}"`
                        : `Add ${selectedActivityIds.size} ${
                            selectedActivityIds.size === 1
                              ? "Exercise"
                              : "Exercises"
                          }`}
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* Create Activity Type Dialog */}
      <CreateActivityTypeDialog
        isOpen={isCreateActivityDialogOpen}
        onClose={() => setIsCreateActivityDialogOpen(false)}
        initialName={searchTerm}
        onActivityTypeCreated={handleActivityTypeCreated}
      />
    </>
  );
};

export default ActivitySelector;
